// src/core/models/global/User.ts
import { z, coerce } from "zod";

function liftCamelToPascal<T extends Record<string, unknown>>(o: unknown): T {
    if (!o || typeof o !== "object") return {} as T;
    const src = o as Record<string, unknown>;
    const out: Record<string, unknown> = { ...src };
    for (const k of Object.keys(src)) {
        if (/^[a-z]/.test(k)) {
            const pascal = k[0].toUpperCase() + k.slice(1);
            if (out[pascal] === undefined) out[pascal] = src[k];
        }
    }
    // normalize common alt names
    if (out.EmailVerified !== undefined && out.isEmailVerified === undefined) {
        out.isEmailVerified = out.EmailVerified;
    }
    if (out.Name !== undefined && out.name_ === undefined) {
        out.name_ = out.Name;
    }
    if (out.CollegesParsed !== undefined && out.collegesParsed === undefined) {
        out.collegesParsed = out.CollegesParsed;
    }
    if (out.ExtraApplicantInfoFields !== undefined && out.extraApplicantInfoFields === undefined) {
        out.extraApplicantInfoFields = out.ExtraApplicantInfoFields;
    }
    if (out.ExtraApplicantInfoCode !== undefined && out.extraApplicantInfoCode === undefined) {
        out.extraApplicantInfoCode = out.ExtraApplicantInfoCode;
    }
    // new CompletedPages normalization
    if (out.CompletedPages !== undefined && out.completedPages === undefined) {
        out.completedPages = out.CompletedPages;
    }
    // SySchoolStatusId normalization
    if (out.SySchoolStatusId !== undefined && out.sySchoolStatusId === undefined) {
        out.sySchoolStatusId = out.SySchoolStatusId;
    }
    return out as T;
}

function nullsToUndefinedDeep(v: unknown): unknown {
    if (v === null) return undefined;
    if (Array.isArray(v)) return v.map(nullsToUndefinedDeep);
    if (v && typeof v === "object") {
        return Object.fromEntries(
            Object.entries(v).map(([k, val]) => [k, nullsToUndefinedDeep(val)])
        );
    }
    return v;
}

const preprocessAll = (v: unknown) => {
    v = liftCamelToPascal(v);
    return nullsToUndefinedDeep(v);
};

const ApplicantPostSecondarySchema = z.object({
    applicantPostSecondaryId: coerce.number().optional(),
    applicantId: coerce.number().optional(),
    applicantDocumentId: coerce.number().optional(),
    nameWhileEnrolled: z.string().optional(),
}).partial();

const FieldValueSchema = z.object({
    fieldValueId: coerce.number().optional(),
    applicantId: coerce.number().optional(),
    applicantEnrollmentId: coerce.number().optional(),
    fieldId: coerce.number().optional(),
    intValue: coerce.number().optional(),
    stringValue: z.string().optional(),
    numericValue: z.number().optional(),
    dateTimeValue: z.string().optional(),
    columnCode: z.string().optional(),
    value: z.string().optional(),
}).partial();

const CompletedPageSchema = z.object({
    applicantId: coerce.number().optional(),
    pageId: z.string().optional(),
}).partial();

const UserObject = z.object({
    applicantId: coerce.number().optional(),
    id: z.string().optional(),
    email: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    studentNumber: z.string().optional(),
    isEmailVerified: coerce.boolean().optional(),
    campusLocationId: coerce.number().optional(),
    programOfInterestId: coerce.number().optional(),
    studentId: z.string().optional(),
    name_: z.string().optional(),
    requiresVerification: coerce.boolean().optional(),
    firstVisit: coerce.boolean().optional(),
    sySchoolStatusId: coerce.number().optional(),

    // new server-backed properties
    collegesParsed: z.array(ApplicantPostSecondarySchema).optional(),
    extraApplicantInfoFields: z.array(FieldValueSchema).optional(),
    extraApplicantInfoCode: z.string().optional(),
    completedPages: z.array(CompletedPageSchema).optional(),
    interestedCampusLocation: z.string().optional(),
}).passthrough();

export type UserShape = z.infer<typeof UserObject>;

export class User implements Omit<UserShape, never> {
    applicantId?: number;
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    studentNumber?: string;
    isEmailVerified?: boolean;
    campusLocationId?: number;
    programOfInterestId?: number;
    studentId?: string;
    name_?: string;
    requiresVerification?: boolean;
    firstVisit?: boolean;
    sySchoolStatusId?: number;

    // new properties
    collegesParsed?: Array<Partial<z.infer<typeof ApplicantPostSecondarySchema>>>;
    extraApplicantInfoFields?: Array<Partial<z.infer<typeof FieldValueSchema>>>;
    extraApplicantInfoCode?: string;
    completedPages?: Array<Partial<z.infer<typeof CompletedPageSchema>>>;
    interestedCampusLocation?: string;

    // capture any additional fields at runtime
    [key: string]: unknown;

    constructor(init?: Partial<UserShape>) {
        if (init) {
            // Filter out properties that would conflict with getters
            const safeProps = Object.fromEntries(
                Object.entries(init as Record<string, unknown>).filter(([key]) => key !== 'name')
            );

            Object.assign(this, safeProps);

            // fallback split if only name_ exists
            if (!this.firstName && !this.lastName && this.name_) {
                const parts = String(this.name_).trim().split(/\s+/);
                this.firstName = parts[0] || "";
                this.lastName = parts.slice(1).join(" ") || "";
            }
        }
    }

    get name(): string {
        return this.firstName && this.lastName ? `${this.firstName} ${this.lastName}` : this.name_ ?? "";
    }

    static schema = z
        .preprocess(preprocessAll, UserObject)
        .transform((v) => {
            const obj = v as Record<string, any>;

            // If server returned ExtraApplicantInfo as a JSON string, parse it into extraApplicantInfoFields
            const rawExtra = obj.extraApplicantInfo ?? obj.ExtraApplicantInfo ?? undefined;
            
            if (typeof rawExtra === 'string' && rawExtra.trim()) {
                try {
                    const parsed = JSON.parse(rawExtra);
                    if (Array.isArray(parsed)) {
                        obj.extraApplicantInfoFields = parsed;
                        // ✅ ALSO set PascalCase variant for safety
                        obj.ExtraApplicantInfoFields = parsed;
                    } else {
                        console.warn('[User.fromJSON] Parsed value is not an array:', parsed);
                    }
                } catch (e) {
                    console.error('[User.fromJSON] Failed to parse extraApplicantInfo:', e);
                    console.error('[User.fromJSON] Raw string was:', rawExtra.substring(0, 200));
                }
            }

            // If extraApplicantInfoFields exists, merge entries into top-level using ColumnCode as key
            const extras = obj.extraApplicantInfoFields ?? obj.ExtraApplicantInfoFields;
            
            if (Array.isArray(extras)) {
                for (const item of extras) {
                    if (!item || typeof item !== 'object') {
                        console.warn('[User.fromJSON] Skipping non-object item:', item);
                        continue;
                    }
                    
                    // try common property names for the code and value
                    const key = item.ColumnCode ?? item.columnCode ?? item.Column ?? item.Code ?? item.FieldCode ?? item.fieldCode;
                    const value = item.Value ?? item.value ?? item.StringValue ?? item.stringValue ?? item.NumericValue ?? item.numericValue ?? item.IntValue ?? item.intValue ?? "";
                   
                    
                    if (key) {
                        const keyStr = String(key);
                        // don't overwrite existing explicit properties
                        if (obj[keyStr] === undefined) obj[keyStr] = value;
                        
                        // Normalize known variants
                        const lower = keyStr.replace(/\s+/g, '').toLowerCase();
                        
                        if (lower === 'hispanicorlatino' || lower === 'ishispanic' || lower === 'ishispanicorlatino' || lower === 'hispanic') {
                            if (obj['HispanicOrLatino'] === undefined) obj['HispanicOrLatino'] = value;
                            if (obj['hispanicOrLatino'] === undefined) obj['hispanicOrLatino'] = value;
                        }
                        if (lower === 'maidenname' || lower === 'maidennname') {
                            if (obj['MaidenName'] === undefined) obj['MaidenName'] = value;
                            if (obj['maidenName'] === undefined) obj['maidenName'] = value;
                        }
                        if (lower === 'racedescription' ) {
                            if (obj['RaceDescription'] === undefined) obj['RaceDescription'] = value;
                            if (obj['raceDescription'] === undefined) obj['raceDescription'] = value;
                        }
                    }
                }
            } else {
                console.warn('[User.fromJSON] extras is not an array, skipping merge');
            }

            const u = new User(obj as Partial<UserShape>);
            return u;
        });

    static fromJSON(raw: unknown): User {
        return User.schema.parse(raw);
    }
}
